import * as React from "react"
import { createPortal } from "react-dom"
import { ArrowLeft, ArrowRight, Check, CircleHelp, X } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  ALFRED_TOUR_STORAGE_KEY,
  consumeAlfredTourAutoStart,
  dispatchAlfredTourAction,
  type AlfredTourAction,
} from "@/lib/alfred-tour"

interface TourFallback {
  target: string
  title: string
  description: string
}

interface TourStep {
  title: string
  description: string
  target?: string
  replayTarget?: string
  route?: string
  section?: "clients" | "devices" | "global"
  includeInClientDeviceReplay?: boolean
  mode?: "bulk"
  activate?: AlfredTourAction
  replayActivate?: AlfredTourAction
  fallbacks?: TourFallback[]
  skipWhen?: string[]
}

const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to Alfred",
    description:
      "Here’s a quick look at what you can do when managing Alfred configuration.",
  },
  {
    title: "Add a client",
    description:
      "Start here to add an Alfred client and define the venue it represents.",
    target: '[data-tour="add-client"]',
    route: "/alfred/clients",
    section: "clients",
  },
  {
    title: "Manage a client",
    description:
      "Use these actions to edit a client or permanently delete it after confirmation.",
    target: '[data-tour="client-actions"]',
    route: "/alfred/clients",
    section: "clients",
    fallbacks: [
      {
        target: '[data-tour="clients-empty"]',
        title: "Client actions appear after setup",
        description:
          "There are no clients to manage yet. Add your first client here; once it exists, you can edit or delete it and open its scoped device list.",
      },
    ],
  },
  {
    title: "View a client’s devices",
    description:
      "View Devices opens the device list scoped to this client.",
    target: '[data-tour="client-devices"]',
    route: "/alfred/clients",
    section: "clients",
    skipWhen: ['[data-tour="clients-empty"]'],
  },
  {
    title: "Add a device",
    description:
      "Open Add Device to add one device or start a bulk add. Individual devices can inherit optional configuration from Alfred’s global defaults.",
    target: '[data-tour="add-device"]',
    route: "/alfred/devices",
    section: "devices",
  },
  {
    title: "Bulk add devices",
    description:
      "Bulk add lets you upload a CSV, validate every row, preview the devices, and then start the import.",
    target: '[data-tour="bulk-add"]',
    replayTarget: '[data-tour="add-device"]',
    route: "/alfred/devices/bulk-add",
    section: "devices",
  },
  {
    title: "Open device details",
    description:
      "You can select a device name or row to open its full details.",
    target: '[data-tour="device-row"]',
    route: "/alfred/devices",
    section: "devices",
    fallbacks: [
      {
        target: '[data-tour="devices-no-results"]',
        title: "No matching devices",
        description:
          "No devices match the current search or filters. Clear or adjust them to restore the list; device details and row actions appear when results are available.",
      },
      {
        target: '[data-tour="devices-empty"]',
        title: "Add your first device",
        description:
          "There are no devices to open yet. Use Add Device or Bulk Add to create them; device details and row actions appear once the list has entries.",
      },
    ],
  },
  {
    title: "Search devices",
    description:
      "Search across fields, then press Enter or choose Search to apply the term.",
    target: '[data-tour="search-devices"]',
    route: "/alfred/devices",
    section: "devices",
  },
  {
    title: "Filter by client",
    description:
      "Narrow the list to one Alfred client and combine that filter with search when you need a more focused result.",
    target: '[data-tour="filter-client"]',
    route: "/alfred/devices",
    section: "devices",
    includeInClientDeviceReplay: false,
  },
  {
    title: "Choose your columns",
    description:
      "Use Columns to show only the configuration fields that matter for the task you are working on.",
    target: '[data-tour="choose-columns"]',
    route: "/alfred/devices",
    section: "devices",
  },
  {
    title: "Sort the list",
    description:
      "Any column heading with arrows can be sorted ascending or descending.",
    target: '[data-tour="sortable-columns"]',
    route: "/alfred/devices",
    section: "devices",
    skipWhen: ['[data-tour="devices-empty"]', '[data-tour="devices-no-results"]'],
  },
  {
    title: "Manage a device",
    description:
      "Use these actions to edit, clone, or delete a device. Deleting always requires confirmation.",
    target: '[data-tour="device-actions"]',
    route: "/alfred/devices",
    section: "devices",
    skipWhen: ['[data-tour="devices-empty"]', '[data-tour="devices-no-results"]'],
  },
  {
    title: "Select devices for bulk actions",
    description:
      "Select one or more devices—or the current page—to reveal the available bulk actions. You can select as many as 200 devices.",
    target: '[data-tour="bulk-select"]',
    route: "/alfred/devices",
    section: "devices",
    skipWhen: ['[data-tour="devices-empty"]', '[data-tour="devices-no-results"]'],
  },
  {
    title: "Bulk edit or delete devices",
    description:
      "Once devices are selected, use Edit Configuration to apply one change across them or Delete to remove them after confirmation.",
    target: '[data-tour="bulk-actions"]',
    replayTarget: '[data-tour="bulk-editor"]',
    route: "/alfred/devices",
    section: "devices",
    mode: "bulk",
    activate: "show-bulk-actions",
    replayActivate: "preview-bulk-actions",
    skipWhen: ['[data-tour="devices-empty"]', '[data-tour="devices-no-results"]'],
  },
  {
    title: "Global configuration",
    description:
      "Global configuration is the top-level Alfred configuration. Devices can inherit these values when they do not define their own overrides.",
    target: '[data-tour="global-config"]',
    route: "/alfred/global",
    section: "global",
  },
  {
    title: "Tutorials are always available",
    description:
      "Select the question mark in the bottom-right corner at any time to replay the tutorial for the page you are viewing.",
    target: '[data-tour="alfred-tour-help"]',
    route: "/alfred/global",
  },
]

interface TourRange {
  stepIndexes: number[]
  returnPath: string
  isReplay: boolean
}

const FULL_TOUR_RANGE: TourRange = {
  stepIndexes: TOUR_STEPS.map((_, index) => index),
  returnPath: "/alfred/clients",
  isReplay: false,
}

function getReplayRange(pathname: string): TourRange | null {
  const isBulkAddPage =
    pathname === "/alfred/devices/bulk-add" ||
    /^\/alfred\/clients\/\d+\/devices\/bulk-add$/.test(pathname)
  if (isBulkAddPage) {
    const bulkAddIndex = TOUR_STEPS.findIndex((step) => step.target === '[data-tour="bulk-add"]')
    return { stepIndexes: [bulkAddIndex], returnPath: pathname, isReplay: true }
  }

  const isClientDevicePage = /^\/alfred\/clients\/\d+\/devices$/.test(pathname)
  const isDevicePage =
    pathname === "/alfred/devices" ||
    isClientDevicePage
  const section: TourStep["section"] | null = pathname === "/alfred/global"
    ? "global"
    : isDevicePage
      ? "devices"
      : pathname === "/" || pathname === "/alfred" || pathname === "/alfred/clients"
        ? "clients"
        : null
  if (!section) return null
  const stepIndexes = TOUR_STEPS.flatMap((step, index) =>
    step.section === section &&
      (!isClientDevicePage || step.includeInClientDeviceReplay !== false)
      ? [index]
      : [],
  )

  return {
    stepIndexes,
    returnPath: pathname,
    isReplay: true,
  }
}

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

function hasSeenTour() {
  try {
    return window.sessionStorage.getItem(ALFRED_TOUR_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

function rememberTour() {
  try {
    window.sessionStorage.setItem(ALFRED_TOUR_STORAGE_KEY, "true")
  } catch {
    // The tour remains usable when browser storage is unavailable.
  }
}

function pruneUnavailableSteps(stepIndexes: number[]) {
  return stepIndexes.filter((index) => {
    const skipWhen = TOUR_STEPS[index].skipWhen
    return !skipWhen?.some((selector) => document.querySelector(selector))
  })
}

function sameStepIndexes(left: number[], right: number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

export function AlfredTour() {
  const location = useLocation()
  const navigate = useNavigate()
  const canReplay = getReplayRange(location.pathname) !== null
  const [open, setOpen] = React.useState(false)
  const [stepIndex, setStepIndex] = React.useState(0)
  const [tourRange, setTourRange] = React.useState<TourRange>(FULL_TOUR_RANGE)
  const [targetRect, setTargetRect] = React.useState<TargetRect | null>(null)
  const [activeFallbackTarget, setActiveFallbackTarget] = React.useState<string | null>(null)
  const [cardHeight, setCardHeight] = React.useState(280)
  const cardRef = React.useRef<HTMLDivElement>(null)
  const step = TOUR_STEPS[stepIndex]
  const stepPosition = tourRange.stepIndexes.indexOf(stepIndex)
  const isLastStep = stepPosition === tourRange.stepIndexes.length - 1
  const visibleStepNumber = stepPosition + 1
  const visibleStepCount = tourRange.stepIndexes.length
  const isAlfredSection =
    location.pathname === "/" || location.pathname.startsWith("/alfred")
  const activeFallback = step.fallbacks?.find(
    (fallback) => fallback.target === activeFallbackTarget,
  )

  React.useEffect(() => {
    if (!consumeAlfredTourAutoStart(location.pathname)) return
    if (!canReplay || hasSeenTour()) return

    setTourRange(FULL_TOUR_RANGE)
    setStepIndex(FULL_TOUR_RANGE.stepIndexes[0])
    setActiveFallbackTarget(null)
    setOpen(true)
  }, [canReplay, location.pathname])

  const cleanUpMode = React.useCallback((mode: TourStep["mode"], isReplay: boolean) => {
    if (mode !== "bulk") return
    dispatchAlfredTourAction(isReplay ? "exit-bulk-preview" : "exit-bulk")
  }, [])

  const closeTour = React.useCallback(() => {
    rememberTour()
    setOpen(false)
    cleanUpMode(TOUR_STEPS[stepIndex].mode, tourRange.isReplay)
  }, [cleanUpMode, stepIndex, tourRange.isReplay])

  const finishTour = React.useCallback(() => {
    rememberTour()
    setOpen(false)
    cleanUpMode(TOUR_STEPS[stepIndex].mode, tourRange.isReplay)
    if (!tourRange.isReplay) navigate(tourRange.returnPath)
  }, [cleanUpMode, navigate, stepIndex, tourRange.isReplay, tourRange.returnPath])

  const startTour = React.useCallback(() => {
    const nextRange = getReplayRange(location.pathname)
    if (!nextRange) return
    const stepIndexes = pruneUnavailableSteps(nextRange.stepIndexes)
    setTourRange({ ...nextRange, stepIndexes })
    setStepIndex(stepIndexes[0])
    setActiveFallbackTarget(null)
    setOpen(true)
    cleanUpMode(TOUR_STEPS[stepIndex].mode, tourRange.isReplay)
  }, [cleanUpMode, location.pathname, stepIndex, tourRange.isReplay])

  const goToStep = React.useCallback((nextPosition: number) => {
    const stepIndexes = pruneUnavailableSteps(tourRange.stepIndexes)
    const currentPosition = stepIndexes.indexOf(stepIndex)
    const direction = nextPosition - stepPosition
    const boundedPosition = Math.max(
      0,
      Math.min(currentPosition + direction, stepIndexes.length - 1),
    )
    const nextIndex = stepIndexes[boundedPosition]
    const nextStep = TOUR_STEPS[nextIndex]
    if (!sameStepIndexes(stepIndexes, tourRange.stepIndexes)) {
      setTourRange((current) => ({ ...current, stepIndexes }))
    }
    setStepIndex(nextIndex)
    setActiveFallbackTarget(null)
    if (step.mode && step.mode !== nextStep.mode) cleanUpMode(step.mode, tourRange.isReplay)
    if (!tourRange.isReplay && nextStep.route && location.pathname !== nextStep.route) {
      navigate(nextStep.route)
    }
  }, [cleanUpMode, location.pathname, navigate, step.mode, stepIndex, stepPosition, tourRange.isReplay, tourRange.stepIndexes])

  const advanceTour = React.useCallback(() => {
    if (isLastStep) {
      finishTour()
      return
    }
    goToStep(stepPosition + 1)
  }, [finishTour, goToStep, isLastStep, stepPosition])

  React.useEffect(() => {
    const action = tourRange.isReplay ? step.replayActivate : step.activate
    if (!open || !isAlfredSection || !action) return
    if (!tourRange.isReplay && step.route && location.pathname !== step.route) return

    const timeout = window.setTimeout(() => {
      dispatchAlfredTourAction(action)
    })
    return () => window.clearTimeout(timeout)
  }, [
    isAlfredSection,
    location.pathname,
    open,
    step.activate,
    step.replayActivate,
    step.route,
    tourRange.isReplay,
  ])

  React.useEffect(() => {
    if (!open || !isAlfredSection) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeTour()
      if (event.key === "ArrowRight") advanceTour()
      if (event.key === "ArrowLeft") goToStep(stepPosition - 1)
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [advanceTour, closeTour, goToStep, isAlfredSection, open, stepPosition])

  React.useLayoutEffect(() => {
    if (!open || !isAlfredSection) return

    let target: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null

    const updateTarget = () => {
      const preferredTarget = step.target
        ? document.querySelector<HTMLElement>(step.target)
        : null
      const fallback = preferredTarget
        ? undefined
        : step.fallbacks?.find(({ target: selector }) =>
            document.querySelector<HTMLElement>(selector),
          )
      const fallbackTarget = fallback
        ? document.querySelector<HTMLElement>(fallback.target)
        : null
      const nextTarget = preferredTarget ?? fallbackTarget ?? (
        tourRange.isReplay && step.replayTarget
          ? document.querySelector<HTMLElement>(step.replayTarget)
          : null
      )

      setActiveFallbackTarget((current) =>
        current === (fallback?.target ?? null) ? current : (fallback?.target ?? null),
      )
      setTourRange((current) => {
        const stepIndexes = pruneUnavailableSteps(current.stepIndexes)
        return sameStepIndexes(stepIndexes, current.stepIndexes)
          ? current
          : { ...current, stepIndexes }
      })

      if (nextTarget !== target) {
        resizeObserver?.disconnect()
        target = nextTarget
        if (target) {
          resizeObserver = new ResizeObserver(updateTarget)
          resizeObserver.observe(target)
        }
      }

      if (!target) {
        setTargetRect(null)
        return
      }

      const rect = target.getBoundingClientRect()
      setTargetRect({
        top: Math.max(8, rect.top - 6),
        left: Math.max(8, rect.left - 6),
        width: rect.width + 12,
        height: rect.height + 12,
      })
    }

    const initialFallback = step.fallbacks?.find(({ target }) =>
      document.querySelector<HTMLElement>(target),
    )
    const initialTarget = (
      step.target ? document.querySelector<HTMLElement>(step.target) : null
    ) ?? (
      initialFallback
        ? document.querySelector<HTMLElement>(initialFallback.target)
        : null
    ) ?? (
      tourRange.isReplay && step.replayTarget
        ? document.querySelector<HTMLElement>(step.replayTarget)
        : null
    )
    initialTarget?.scrollIntoView?.({ behavior: "smooth", block: "center", inline: "nearest" })
    updateTarget()

    const mutationObserver = new MutationObserver(updateTarget)
    mutationObserver.observe(document.body, { childList: true, subtree: true })
    window.addEventListener("resize", updateTarget)
    window.addEventListener("scroll", updateTarget, true)

    return () => {
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener("resize", updateTarget)
      window.removeEventListener("scroll", updateTarget, true)
    }
  }, [isAlfredSection, open, step.fallbacks, step.replayTarget, step.target, tourRange.isReplay])

  React.useLayoutEffect(() => {
    if (!open || !isAlfredSection || !cardRef.current) return
    cardRef.current.focus()
    setCardHeight(cardRef.current.getBoundingClientRect().height || 280)
  }, [isAlfredSection, open, stepIndex])

  const cardStyle = React.useMemo<React.CSSProperties>(() => {
    if (!targetRect) {
      return {
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }
    }

    const cardWidth = Math.min(384, window.innerWidth - 32)
    const gap = 16
    const centeredLeft = Math.min(
      window.innerWidth - cardWidth - 16,
      Math.max(16, targetRect.left + targetRect.width / 2 - cardWidth / 2),
    )
    const canPlaceBelow =
      targetRect.top + targetRect.height + gap + cardHeight <= window.innerHeight
    const canPlaceAbove = targetRect.top - cardHeight - gap >= 16

    if (canPlaceBelow) {
      return {
        left: centeredLeft,
        top: targetRect.top + targetRect.height + gap,
      }
    }
    if (canPlaceAbove) {
      return {
        left: centeredLeft,
        top: targetRect.top - cardHeight - gap,
      }
    }

    const left = targetRect.left - cardWidth - gap >= 16
      ? targetRect.left - cardWidth - gap
      : Math.min(window.innerWidth - cardWidth - 16, targetRect.left + targetRect.width + gap)
    const top = Math.min(
      window.innerHeight - cardHeight - 16,
      Math.max(16, targetRect.top + targetRect.height / 2 - cardHeight / 2),
    )

    return { left, top }
  }, [cardHeight, targetRect])

  if (!isAlfredSection) return null

  return (
    <TooltipProvider delayDuration={300}>
      {canReplay ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={startTour}
              data-tour="alfred-tour-help"
              className="fixed bottom-4 right-4 z-40 rounded-full shadow-lg"
              aria-label="Open Alfred tutorial"
            >
              <CircleHelp />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Show Alfred tutorial</TooltipContent>
        </Tooltip>
      ) : null}

      {open
        ? createPortal(
            <div className="fixed inset-0 z-[60]" data-testid="alfred-tour">
              {!targetRect ? (
                <div className="absolute inset-0 bg-black/65" aria-hidden="true" />
              ) : null}
              {targetRect ? (
                <div
                  className="pointer-events-none fixed rounded-lg ring-4 ring-primary ring-offset-2 ring-offset-background"
                  style={{
                    ...targetRect,
                    boxShadow: "0 0 0 9999px rgb(0 0 0 / 0.65)",
                  }}
                  aria-hidden="true"
                  data-testid="tour-spotlight"
                />
              ) : null}

              <Card
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="alfred-tour-title"
                aria-describedby="alfred-tour-description"
                tabIndex={-1}
                className="fixed w-[calc(100vw-2rem)] max-w-sm border-primary/40 shadow-2xl max-sm:bottom-20 max-sm:left-4 max-sm:top-auto max-sm:transform-none"
                style={cardStyle}
              >
                <CardHeader className="gap-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-primary">
                        Alfred · {visibleStepNumber} of {visibleStepCount}
                      </p>
                      <CardTitle id="alfred-tour-title" className="text-xl">
                        {activeFallback?.title ?? step.title}
                      </CardTitle>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={closeTour}
                      aria-label="Quit tutorial"
                    >
                      <X />
                    </Button>
                  </div>
                  <Progress
                    value={(visibleStepNumber / visibleStepCount) * 100}
                    className="h-1.5"
                    aria-label={`Tutorial progress: step ${visibleStepNumber} of ${visibleStepCount}`}
                  />
                </CardHeader>
                <CardContent>
                  <CardDescription id="alfred-tour-description" className="text-sm leading-6">
                    {activeFallback?.description ?? step.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="justify-between gap-3">
                  <Button type="button" variant="ghost" onClick={closeTour}>
                    Skip tutorial
                  </Button>
                  <div className="flex gap-2">
                    {stepPosition > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => goToStep(stepPosition - 1)}
                      >
                        <ArrowLeft data-icon="inline-start" />
                        Back
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      onClick={advanceTour}
                    >
                      {isLastStep ? (
                        <>
                          <Check data-icon="inline-start" />
                          Finish
                        </>
                      ) : (
                        <>
                          Next
                          <ArrowRight data-icon="inline-end" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </div>,
            document.body,
          )
        : null}
    </TooltipProvider>
  )
}
